import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { firstValueFrom, catchError, throwError, of, map } from 'rxjs';
import { SKIP_ERROR_NOTIFICATION } from '@core/error-handling/services/http-error-notification.interceptor';
import { environment } from '@environments/environment';
import { API_ENDPOINTS } from '@shared/constants';

export interface DiasQuadroHorario {
  diasDaSemana: number[]; // 0=domingo ... 6=sábado
}

export interface FrequenciaDia {
  id?: number;
  enturmacao: number | { id: number };
  data: string;
  tipo: 'PRESENTE' | 'FALTA' | 'DISPENSADO' | 'PRESENCA_REMOTA' | null;
  justificativa?: string;
  periodoAvaliativo?: { id: number };
  version?: number;
  resultadoFrequencia?: {
    faltas: number;
    percentual: number;
  };
  registroFaltas?: {
    id?: number;
    tipo?: string;
    justificativa?: string;
  };
  $$tipo?: string | null;
  $$isChecked?: boolean;
  $$disabled?: boolean;
  $$hasDispensa?: boolean;
  isEnturmado?: boolean;
}

export interface Enturmacao {
  id: number;
  nome?: string;
  nomeSocial?: string;
  numeroMatricula?: string;
  numeroChamada?: number;
  dataInicio?: string;
  dataFim?: string;
  frequenciasDias?: FrequenciaDia[];
  presente?: boolean;
  falta?: boolean;
  dispensado?: boolean;
  presencaRemota?: boolean;
  justificativa?: string;
  frequenciaId?: number | null;
  frequenciaVersion?: number | null;
  /** Frequência completa, preservada para manter o período original ao editar. */
  frequenciaDia?: FrequenciaDia | null;
  totalFaltas?: number;
  frequenciaPercentual?: string;
  foto?: string;
  desabilitado?: boolean;  // aluno não enturmado no dia selecionado
  matricula?: {
    id?: number;
    nome?: string;
    nomeFantasia?: string;  // nome social (campo real retornado pela API)
    nomeSocial?: string;    // nome social (campo alternativo)
    idAluno?: number;
    numeroMatricula?: string;
    aluno?: {
      id?: number;
      nome?: string;
      nomeSocial?: string;
      nomeFantasia?: string;
      pessoa?: {
        id?: number;
        nome?: string;
      };
    };
  };
  aluno?: {
    id?: number;
    nome?: string;
    nomeSocial?: string;
    nomeFantasia?: string;
    numeroMatricula?: string;
  };
  enturmacao?: {
    id?: number;
    aluno?: {
      id?: number;
      nome?: string;
      numeroMatricula?: string;
    };
    pessoa?: {
      id?: number;
      nome?: string;
    };
    numeroChamada?: number;
    dataInicio?: string;
    dataFim?: string;
  };
  pessoa?: {
    id?: number;
    nome?: string;
  };
}

export interface PeriodoAvaliativo {
  id: number;
  descricao: string;
  dataInicial: string;
  dataFinal: string;
}

export interface EncerramentoTurma {
  id: number;
  status: 'NAO_INICIADO' | 'EM_ABERTO' | 'PROCESSANDO' | 'PROCESSANDO_REABERTURA' | 'ENCERRADO';
  tipoPeriodo?: string;
  periodoAvaliativo?: {
    id: number;
    descricao?: string;
  };
  identificadorExameFinal?: string;
  periodoId?: number;
}

export interface TurmaCompleta {
  id: number;
  descricao?: string;
  tipoAtendimento?: string;
  etapaMatriz?: {
    modular?: boolean;
  };
  /** Turma multiturno: frequência por aula deve cruzar turno do aluno com turnos da aula (hora-aula). */
  multiturno?: boolean;
  /** Quadro hora-aula (itens por dia da semana + durações por número de aula + turnos). */
  horaAula?: {
    itens?: Array<{
      diasDaSemana?: Array<string | number>;
      itensDuracao?: Array<{ nroAula?: number; numero?: number; turnos?: string[] }>;
    }>;
  };
}

export interface DiasDoMes {
  dias: string[]; // YYYY-MM-DD
}

export interface MotivoAbonoJustificativa {
  id: number;
  descricao: string;
  tipo: 'ABONO' | 'JUSTIFICATIVA';
  ativo?: boolean;
}

export interface PaginatedResponse<T> {
  offset: number;
  limit: number;
  total: number;
  hasNext: boolean;
  content: T[];
}

export interface FaltaDia {
  id: number;
  data: string;
  enturmacao: {
    id: number;
  };
  tipo: 'FALTA';
  // alocacaoAula/itemEducacional só presentes no retorno de /falta-aula (modo por aula)
  alocacaoAula?: {
    numero?: number;
    data?: string;
  };
  itemEducacional?: {
    id?: number;
    descricao?: string;
  };
}

export interface Abono {
  id?: number;
  enturmacao: {
    id: number;
  };
  motivo: {
    id: number;
    tipo: 'ABONO' | 'JUSTIFICATIVA';
    descricao?: string;
  };
  faltasDias?: Array<{   // usado quando tipoAbono = ABONO_FREQUENCIA_DIA
    id: number;
    data: string;
    descricao: string;
  }>;
  faltasAulas?: Array<{  // usado quando tipoAbono = ABONO_FREQUENCIA_AULA
    id: number;
    data: string;
    descricao: string;
  }>;
  observacao?: string;
  tipoAbono?: 'ABONO_FREQUENCIA_DIA' | 'ABONO_FREQUENCIA_AULA';
  dataCriacao?: string;
}

export interface Justificativa {
  id?: number;
  enturmacao: {
    id: number;
  };
  motivo: {
    id: number;
    tipo: 'ABONO' | 'JUSTIFICATIVA';
    descricao?: string;
  };
  faltasDias?: Array<{  // usado quando tipoJustificativa = JUSTIFICATIVA_FREQUENCIA_DIA
    id: number;
    data?: string;
    descricao?: string;
  }>;
  faltas?: Array<{      // usado quando tipoJustificativa = JUSTIFICATIVA_FREQUENCIA_AULA
    id: number;
    data?: string;
    descricao?: string;
  }>;
  justificativa: string; // texto livre, máx 4000 chars
  tipoJustificativa?: 'JUSTIFICATIVA_FREQUENCIA_DIA' | 'JUSTIFICATIVA_FREQUENCIA_AULA';
  dataCriacao?: string;
}

/** Retornada por GET /api/frequencia/abono-justificativa/falta-dia */
export interface AbonoJustificativaListItem {
  id: number;
  abonosJustificativasId?: number;
  data?: string;
  tipo: 'ABONO' | 'JUSTIFICATIVA';
  motivo?: {
    id: number;
    tipo?: 'ABONO' | 'JUSTIFICATIVA';
    descricao: string;
  };
  justificativa?: string;
  observacao?: string;
  dataCriacao?: string;
  faltas?: Array<{
    id: number;
    data?: string;            // presente em falta-dia
    alocacaoAula?: {
      numero?: number;
      data?: string;          // presente em falta-aula (data da aula)
    };
    itemEducacional?: {       // presente em falta-aula no nível raiz
      id?: number;
      descricao?: string;
    };
  }>;
}

/** Retornada por GET /api/frequencia/frequencia/registros-abonos/nao-justificadas */
export interface FaltasRestantesItem {
  itemEducacional: { id: number };
  faltasNaoJustificadas: number;
  enturmacao?: { id: number };
  periodoAvaliativo?: { id: number };
}

@Injectable({
  providedIn: 'root'
})
export class MobileFrequenciaService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /** Para turmas de atividade, retorna dataInicio/dataFim que servem como período. */
  async getItemEducacional(itemEducacionalId: number): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(
        `${this.apiUrl}/professores/item-educacional/${itemEducacionalId}`,
        { context: new HttpContext().set(SKIP_ERROR_NOTIFICATION, true) }
      )
    );
  }

  getEnturmacoesFrequencia(turmaId: number, filter?: string): Promise<Enturmacao[]> {
    let params = new HttpParams();

    if (filter) {
      params = params.set('filter', filter);
    }

    return firstValueFrom(
      this.http.get<Enturmacao[]>(
        `${this.apiUrl}/frequencia/turma/${turmaId}/frequencias/enturmacoes`,
        { params, context: new HttpContext().set(SKIP_ERROR_NOTIFICATION, true) }
      )
    );
  }

  async getPeriodos(turmaId: number): Promise<PeriodoAvaliativo[]> {
    const params = new HttpParams()
      .set('turma', turmaId.toString());

    try {
      const periodos = await firstValueFrom(
        this.http.get<PeriodoAvaliativo[]>(`${this.apiUrl}/commons/periodos-avaliativos`,
          { params, context: new HttpContext().set(SKIP_ERROR_NOTIFICATION, true) })
      );

      if (!periodos || periodos.length === 0) {
        return [];
      }

      return periodos;
    } catch (error: unknown) {
      // 404: turma pode não ter períodos configurados ainda
      const errorObj = error as { status?: number };
      if (errorObj.status === 404) {
        return [];
      }
      throw error;
    }
  }

  /** @deprecated Usar criarFalta() ou removePorDia() conforme o caso */
  savePorDiaIndividual(frequencia: FrequenciaDia, context?: HttpContext): Promise<FrequenciaDia> {
    return firstValueFrom(
      this.http.post<FrequenciaDia[]>(`${this.apiUrl}/frequencia/falta-dia`, [frequencia], context ? { context } : {})
    ).then(resultado => resultado[0]);
  }

  /** Backend espera payload completo com aulaRealizada. */
  criarFalta(payload: any): Promise<any> {
    return firstValueFrom(
      this.http.post(`${this.apiUrl}/frequencia/falta-dia`, payload)
    );
  }

  removePorDia(id: number): Promise<any> {
    return firstValueFrom(
      this.http.delete(`${this.apiUrl}/frequencia/falta-dia/${id}`)
    );
  }

  /** Qualquer erro de request é tratado como "turma sem configuração". */
  async buscarConfiguracaoFrequenciaTurma(turmaId: number): Promise<any | null> {
    const endpoint = API_ENDPOINTS.FREQUENCIA.CONFIGURACAO_FREQUENCIA.replace('{turmaId}', String(turmaId));
    try {
      return await firstValueFrom(
        this.http.get(`${this.apiUrl}${endpoint}`, {
          context: new HttpContext().set(SKIP_ERROR_NOTIFICATION, true)
        })
      );
    } catch {
      return null;
    }
  }

  async getTurmaCompleta(turmaId: number): Promise<TurmaCompleta> {
    const params = new HttpParams()
      .set('filter', `id=${turmaId}`)
      .set('offset', '0')
      .set('limit', '1');

    const response = await firstValueFrom(
      this.http.get<{ content?: TurmaCompleta[] }>(
        `${this.apiUrl}/context/turma`,
        { params, context: new HttpContext().set(SKIP_ERROR_NOTIFICATION, true) }
      )
    );

    const raw = response?.content?.[0] ?? { id: turmaId };
    const v = raw as TurmaCompleta & { values?: { multiturno?: boolean; horaAula?: TurmaCompleta['horaAula'] } };
    return {
      ...raw,
      multiturno: v.multiturno ?? v.values?.multiturno,
      horaAula: v.horaAula ?? v.values?.horaAula
    };
  }

  /** @deprecated Use getFrequenciaPorMesAno() para buscar sem filtrar por período */
  getFrequenciaDia(
    turmaId: number,
    periodoId: number,
    mes: number,
    ano: number
  ): Promise<FrequenciaDia[]> {
    const params = new HttpParams()
      .set('ano', ano.toString())
      .set('mes', mes.toString())
      .set('offset', '0')
      .set('limit', '500')
      .set('filter', '');

    return firstValueFrom(
      this.http.get<{ content: FrequenciaDia[]; total: number; offset: number; limit: number; hasNext: boolean }>(
        `${this.apiUrl}/frequencia/falta-dia/turma/${turmaId}/periodo/${periodoId}`,
        { params, context: new HttpContext().set(SKIP_ERROR_NOTIFICATION, true) }
      ).pipe(
        // Endpoint /falta-dia/ retorna apenas faltas, sem campo 'tipo'
        map(response => (response.content || []).map(f => ({
          ...f,
          tipo: 'FALTA' as const
        }))),
        catchError(error => {
          return of([]);
        })
      )
    );
  }

  /** Diferente de getFrequenciaDia: retorna todos os tipos (PRESENTE/FALTA/DISPENSADO/PRESENCA_REMOTA). */
  getFrequenciaPorMesAno(
    turmaId: number,
    periodoId: number,
    mes: number,
    ano: number
  ): Promise<FrequenciaDia[]> {
    const params = new HttpParams()
      .set('mes', mes.toString())
      .set('ano', ano.toString())
      .set('offset', '0')
      .set('limit', '500');

    // ngsw-bypass: registros de falta/dispensa mudam a cada interação, não pode ficar stale.
    const headers = new HttpHeaders({ 'ngsw-bypass': 'true' });

    return firstValueFrom(
      this.http.get<{ content: FrequenciaDia[]; total: number; offset: number; limit: number; hasNext: boolean }>(
        `${this.apiUrl}/frequencia/falta-dia/turma/${turmaId}/periodo/${periodoId}`,
        { params, headers, context: new HttpContext().set(SKIP_ERROR_NOTIFICATION, true) }
      ).pipe(
        map(response => (response.content || []).map(f => ({
          ...f,
          tipo: 'FALTA' as const
        }))),
        catchError(error => {
          return of([]);
        })
      )
    );
  }

  async getAulasRealizadasDia(
    turmaId: number,
    periodoId: number,
    mes: number,
    ano: number
  ): Promise<any[]> {
    const params = new HttpParams()
      .set('mes', mes.toString())
      .set('ano', ano.toString());

    // ngsw-bypass: estado do checkbox e ícones P/F/D dependem desta resposta, não pode ficar stale.
    const headers = new HttpHeaders({ 'ngsw-bypass': 'true' });

    try {
      const response = await firstValueFrom(
        this.http.get<any[]>(
          `${this.apiUrl}/frequencia/aula-realizada-dia/turma/${turmaId}/periodo/${periodoId}/aulas-dia-realizadas`,
          { params, headers, context: new HttpContext().set(SKIP_ERROR_NOTIFICATION, true) }
        )
      );
      return response || [];
    } catch (error: any) {
      return [];
    }
  }

  /** Aplica presença automática a todos os alunos, exceto os com falta. */
  async saveAulaRealizada(payload: {
    data: string;
    turma: { id: number };
    periodoAvaliativo: { id: number };
  }): Promise<any> {
    const response = await firstValueFrom(
      this.http.post(`${this.apiUrl}/frequencia/aula-realizada-dia`, payload)
    );
    return response;
  }

  /** Ao desmarcar, remove a aula e reverte presenças. */
  async deleteAulaRealizada(id: number): Promise<any> {
    const response = await firstValueFrom(
      this.http.delete(`${this.apiUrl}/frequencia/aula-realizada-dia/${id}`)
    );
    return response;
  }

  /** Sem filtro de mês/ano. */
  getFrequenciasPeriodo(
    turmaId: number,
    periodoId: number
  ): Promise<FrequenciaDia[]> {
    
    return firstValueFrom(
      this.http.get<FrequenciaDia[]>(
        `${this.apiUrl}/frequencia/falta-dia/turma/${turmaId}/periodo/${periodoId}`,
        { context: new HttpContext().set(SKIP_ERROR_NOTIFICATION, true) }
      ).pipe(
        catchError(error => {
          return of([]);
        })
      )
    );
  }

  async getDiasQuadroHorario(
    turmaId: number,
    periodoId: number,
    itemEducacionalIds?: number[],
    data?: string
  ): Promise<DiasQuadroHorario> {
    try {
      let params = new HttpParams()
        .set('periodoId', periodoId.toString());
      
      (itemEducacionalIds || []).forEach((itemEducacionalId) => {
        params = params.append('itemEducacional', itemEducacionalId.toString());
      });

      if (data) {
        params = params.set('data', data);
      }

      const response = await firstValueFrom(
        this.http.get<DiasQuadroHorario>(
          `${this.apiUrl}/frequencia/falta-aula/turma/${turmaId}/dias-quadro-horario`,
          { params, context: new HttpContext().set(SKIP_ERROR_NOTIFICATION, true) }
        )
      );
      return response || { diasDaSemana: [1, 2, 3, 4, 5] }; // Default: seg a sex
    } catch (error) {
      return { diasDaSemana: [1, 2, 3, 4, 5] }; // Fallback: seg a sex
    }
  }

  /**
   * Busca resultados/estatísticas de frequência (faltas totais e percentuais)
   * Endpoint: GET /api/frequencia/turma/{turmaId}/frequencias/resultados
   * AngularJS: getFaltasTotais() em frequencia.service.js (linha 162)
   * Retorna array com estatísticas por aluno (enturmacao)
   */
  getFaltasTotais(turmaId: number, periodoId: number): Promise<any[]> {
    const params = new HttpParams()
      .set('periodo', periodoId.toString());

    return firstValueFrom(
      this.http.get<any[]>(
        `${this.apiUrl}/frequencia/turma/${turmaId}/frequencias/resultados`,
        { params }
      )
    );
  }

  /** Retorna apenas os dias válidos considerando período, quadro de horários e calendário. */
  async getDiasDoMes(mes: number, ano: number, periodoId: number): Promise<DiasDoMes> {
    const params = new HttpParams()
      .set('mes', mes.toString())
      .set('ano', ano.toString())
      .set('periodoAvaliativo', periodoId.toString());

    const response = await firstValueFrom(
      this.http.get<DiasDoMes>(
        `${this.apiUrl}/frequencia/falta-dia/dias-mes`,
        { params, context: new HttpContext().set(SKIP_ERROR_NOTIFICATION, true) }
      )
    );

    return response;
  }

  async getEncerramentosTurma(turmaId: number): Promise<EncerramentoTurma[]> {
    try {
      const response = await firstValueFrom(
        this.http.get<EncerramentoTurma[]>(
          `${this.apiUrl}/commons/encerramento/encerramentos-turma/${turmaId}`,
          // Serviço já tem fallback (retorna []) — suprimir toast global
          { context: new HttpContext().set(SKIP_ERROR_NOTIFICATION, true) }
        )
      );

      return response;
    } catch (error) {
      return [];
    }
  }

  // ===================================================================
  // MÉTODOS DE ABONO E JUSTIFICATIVA DE FALTAS
  // ===================================================================

  async getAbonosJustificativasFaltasDias(
    enturmacaoId: number,
    periodoAvaliativoId: number
  ): Promise<PaginatedResponse<AbonoJustificativaListItem>> {
    // Só adiciona filtro de período se id é truthy (turmas de atividade complementar usam período sintético com id=0)
    let filter = `enturmacao.id = ${enturmacaoId}`;
    if (periodoAvaliativoId) {
      filter += ` and (abono.periodoAvaliativo.id = ${periodoAvaliativoId} or justificativa.periodoAvaliativo.id = ${periodoAvaliativoId})`;
    }

    const params = new HttpParams()
      .set('filter', filter)
      .set('limit', '25')
      .set('offset', '0')
      .set('sort', '');

    const url = `${this.apiUrl}/frequencia/abono-justificativa/falta-dia`;
    // Header filter-encoded: true é necessário para filtros complexos
    const headers = { 'filter-encoded': 'true' };

    return await firstValueFrom(
      this.http.get<PaginatedResponse<AbonoJustificativaListItem>>(url, { params, headers })
    );
  }

  async getFaltasDiasSemAbonos(
    enturmacaoId: number,
    periodoAvaliativoId: number
  ): Promise<FaltaDia[]> {
    const filter = periodoAvaliativoId ? `periodoAvaliativo = ${periodoAvaliativoId}` : '';
    const params = filter ? new HttpParams().set('filter', filter) : new HttpParams();

    const url = `${this.apiUrl}/frequencia/falta-dia/enturmacao/${enturmacaoId}/sem-abonos-justificavas`;

    const headers = { 'filter-encoded': 'true' };

    return await firstValueFrom(
      this.http.get<FaltaDia[]>(url, { params, headers })
    );
  }

  /**
   * Busca faltas de AULAS sem abono/justificativa para o aluno no período
   * Equivalente de getFaltasDiasSemAbonos para o modo frequência por aula
   */
  async getFaltasAulasSemAbonos(
    enturmacaoId: number,
    periodoAvaliativoId: number,
    itemEducacionalIds?: number[]
  ): Promise<FaltaDia[]> {
    // AngularJS: só adiciona filtro de período se id é truthy (turmas de atividade complementar usam período sintético com id=0)
    let filter = periodoAvaliativoId ? `periodoAvaliativo = ${periodoAvaliativoId}` : '';
    if (itemEducacionalIds && itemEducacionalIds.length > 0) {
      filter += (filter ? ' and ' : '') + `itemEducacional in (${itemEducacionalIds.join(', ')})`;
    }
    const params = filter ? new HttpParams().set('filter', filter) : new HttpParams();
    const url = `${this.apiUrl}/frequencia/falta-aula/enturmacao/${enturmacaoId}/sem-abonos-justificavas`;
    const headers = { 'filter-encoded': 'true' };

    return await firstValueFrom(
      this.http.get<FaltaDia[]>(url, { params, headers })
    );
  }

  /**
   * Busca abonos e justificativas de FALTAS DE AULA do aluno no período
   * Equivalente de getAbonosJustificativasFaltasDias para o modo por aula
   */
  async getAbonosJustificativasFaltasAulas(
    enturmacaoId: number,
    periodoAvaliativoId: number,
    itemEducacionalIds?: number[],
    isEjaModular?: boolean
  ): Promise<PaginatedResponse<AbonoJustificativaListItem>> {
    let filter = `enturmacao.id = ${enturmacaoId}`;
    if (!isEjaModular && itemEducacionalIds && itemEducacionalIds.length > 0) {
      filter += ` and (abono.falta.itemEducacional.id in (${itemEducacionalIds.join(',')}) or justificativa.falta.itemEducacional.id in (${itemEducacionalIds.join(',')}))`;
    }
    // AngularJS: só adiciona filtro de período se id é truthy (turmas de atividade complementar usam período sintético com id=0)
    if (periodoAvaliativoId) {
      filter += ` and (abono.periodoAvaliativo.id = ${periodoAvaliativoId} or justificativa.periodoAvaliativo.id = ${periodoAvaliativoId})`;
    }
    const params = new HttpParams()
      .set('filter', filter)
      .set('limit', '25')
      .set('offset', '0')
      .set('sort', '');
    const url = `${this.apiUrl}/frequencia/abono-justificativa/falta-aula`;
    const headers = { 'filter-encoded': 'true' };

    return await firstValueFrom(
      this.http.get<PaginatedResponse<AbonoJustificativaListItem>>(url, { params, headers })
    );
  }

  /**
   * Busca motivos cadastrados para abono
   * Endpoint: GET /api/commons/motivo-abono-justificativa
   * AngularJS: getMotivosAbonoJustificativa() em abono-justificativa.services.js
   * 
   * @returns Lista de motivos ativos do sistema (ED-28151)
   */
  async getMotivosAbonoJustificativa(): Promise<PaginatedResponse<MotivoAbonoJustificativa>> {
    const url = `${this.apiUrl}/commons/motivo-abono-justificativa`;

    return await firstValueFrom(
      this.http.get<PaginatedResponse<MotivoAbonoJustificativa>>(url)
    );
  }

  /**
   * Busca últimas 5 justificativas do professor
   * Endpoint: GET /api/frequencia/justificativa/justificativas-recentes
   * AngularJS: getJustificativasRecentes() em cadastro-justificativa-dias-falta.controller.js
   * 
   * Usado para autocompletar campo de justificativa
   * @returns Array com últimas 5 justificativas (texto livre)
   */
  async getJustificativasRecentes(): Promise<string[]> {
    try {
      const response = await firstValueFrom(
        this.http.get<string[]>(
          `${this.apiUrl}/frequencia/justificativa/justificativas-recentes`
        )
      );

      return response;
    } catch (error) {
      return []; // Retorna array vazio em caso de erro
    }
  }

  /**
   * Compatibilidade com chamadas que esperam lista de justificativas
   * no formato { id, descricao }.
   */
  async getJustificativas(): Promise<Array<{ id: number; descricao: string }>> {
    const recentes = await this.getJustificativasRecentes();
    return (recentes || []).map((descricao, index) => ({
      id: index + 1,
      descricao
    }));
  }

  /**
   * Cria ou atualiza um abono
   * Endpoint: POST /api/frequencia/abono (criar) ou PUT /api/frequencia/abono/{id} (atualizar)
   * AngularJS: save() em modal-abono-justificativa.service.js
   * 
   * @param abono Dados do abono (com ou sem ID)
   * @returns Abono criado/atualizado
   */
  async saveAbono(abono: Abono): Promise<Abono> {
    const isUpdate = !!abono.id;

    if (isUpdate) {
      // PUT - Atualização
      return await firstValueFrom(
        this.http.put<Abono>(
          `${this.apiUrl}/frequencia/abono/${abono.id}`,
          abono
        )
      );
    } else {
      // POST - Criação
      return await firstValueFrom(
        this.http.post<Abono>(`${this.apiUrl}/frequencia/abono`, abono)
      );
    }
  }

  /**
   * Cria ou atualiza uma justificativa
   * Endpoint: POST /api/frequencia/justificativa (criar) ou PUT /api/frequencia/justificativa/{id} (atualizar)
   * AngularJS: save() em modal-abono-justificativa.service.js
   * 
   * @param justificativa Dados da justificativa (com ou sem ID)
   * @returns Justificativa criada/atualizada
   */
  async saveJustificativa(justificativa: Justificativa): Promise<Justificativa> {
    const isUpdate = !!justificativa.id;

    if (isUpdate) {
      // PUT - Atualização
      return await firstValueFrom(
        this.http.put<Justificativa>(
          `${this.apiUrl}/frequencia/justificativa/${justificativa.id}`,
          justificativa
        )
      );
    } else {
      // POST - Criação
      return await firstValueFrom(
        this.http.post<Justificativa>(
          `${this.apiUrl}/frequencia/justificativa`,
          justificativa
        )
      );
    }
  }

  /**
   * Remove um abono
   * Endpoint: DELETE /api/frequencia/abono/{id}
   * AngularJS: remove() em modal-abono-justificativa.service.js
   * 
   * @param abonoId ID do abono a ser removido
   */
  async removeAbono(abonoId: number): Promise<void> {
    await firstValueFrom(
      this.http.delete(`${this.apiUrl}/frequencia/abono/${abonoId}`)
    );
  }

  /**
   * Remove uma justificativa
   * Endpoint: DELETE /api/frequencia/justificativa/{id}
   * AngularJS: remove() em modal-abono-justificativa.service.js
   * 
   * @param justificativaId ID da justificativa a ser removida
   */
  async removeJustificativa(justificativaId: number): Promise<void> {
    await firstValueFrom(
      this.http.delete(`${this.apiUrl}/frequencia/justificativa/${justificativaId}`)
    );
  }

  /**
   * Busca registros de faltas de uma turma para obter o itemEducacionalPeriodo
   * Endpoint: GET /api/frequencia/turma/{turmaId}/frequencias/registros-faltas
   * AngularJS: FrequenciaService.getRegistrosFaltas() em frequencia.service.js
   *
   * Necessário para falta-dia: o endpoint de faltas restantes (nao-justificadas)
   * requer o itemEducacional do período, que é obtido a partir deste endpoint.
   *
   * @param turmaId ID da turma
   * @param enturmacaoId ID da enturmação (usado no filtro)
   * @param periodoAvaliativoId ID do período avaliativo (usado no filtro)
   * @returns Lista de registros de faltas contendo itemEducacional
   */
  async getRegistrosFaltas(
    turmaId: number,
    enturmacaoId: number,
    periodoAvaliativoId: number
  ): Promise<any[]> {
    try {
      let filter = `enturmacao.id = ${enturmacaoId}`;
      if (periodoAvaliativoId) {
        filter += ` and periodoAvaliativo.id = ${periodoAvaliativoId}`;
      }

      const params = new HttpParams()
        .set('filter', filter)
        .set('isAtividadeOrModular', 'false');

      const headers = { 'filter-encoded': 'true' };
      const url = `${this.apiUrl}/frequencia/turma/${turmaId}/frequencias/registros-faltas`;
      console.log('📥 [Service] Buscando registros de faltas:', { url, filter });

      const response = await firstValueFrom(
        this.http.get<any[]>(url, { params, headers, context: new HttpContext().set(SKIP_ERROR_NOTIFICATION, true) })
      );

      console.log('✅ [Service] Registros de faltas recebidos:', response);
      return response || [];
    } catch (error: any) {
      console.error('❌ [Service] Erro ao buscar registros de faltas:', error);
      return [];
    }
  }

  /**
   * Busca faltas restantes (não justificadas) para verificar limites do sistema Educação
   * Endpoint: GET /api/frequencia/frequencia/registros-abonos/nao-justificadas
   * AngularJS: AbonoJustificativaService.getFaltasRestantes() em modal-abono-justificativa.service.js
   *
   * Retorna o número de faltas que ainda podem ser abonadas/justificadas.
   * Quando o total difere das faltas sem abono, significa que há faltas
   * registradas no sistema Educação que só podem ser gerenciadas por lá.
   *
   * @param enturmacaoId ID da enturmação
   * @param periodoAvaliativoId ID do período avaliativo
   * @param itensEducacionais ID(s) dos itens educacionais
   * @returns Lista de itens com contagem de faltas não justificadas
   */
  async getFaltasRestantes(
    enturmacaoId: number,
    periodoAvaliativoId: number,
    itensEducacionais: number | number[]
  ): Promise<FaltasRestantesItem[]> {
    try {
      let params = new HttpParams()
        .set('enturmacao', enturmacaoId.toString());

      if (periodoAvaliativoId) {
        params = params.set('periodoAvaliativo', periodoAvaliativoId.toString());
      }

      // itensEducacionais pode ser um ID único (falta-dia) ou array (falta-aula)
      if (Array.isArray(itensEducacionais)) {
        params = params.set('itensEducacionais', itensEducacionais.join(','));
      } else {
        params = params.set('itensEducacionais', itensEducacionais.toString());
      }

      const url = `${this.apiUrl}/frequencia/frequencia/registros-abonos/nao-justificadas`;
      console.log('📥 [Service] Buscando faltas restantes:', { url, enturmacaoId, periodoAvaliativoId, itensEducacionais });

      const response = await firstValueFrom(
        this.http.get<FaltasRestantesItem[]>(url, { params, context: new HttpContext().set(SKIP_ERROR_NOTIFICATION, true) })
      );

      console.log('✅ [Service] Faltas restantes recebidas:', response);
      return response || [];
    } catch (error: any) {
      console.error('❌ [Service] Erro ao buscar faltas restantes:', error);
      return [];
    }
  }
}